import { useEffect, useRef } from 'react';
import { logicalDate } from '@selflevel/domain';
import { recordFocusSession } from '../store/mutations';
import { getSettings } from '../store/session';
import { useFocus } from './foco-estado';
import { avisarPresencia } from './foco-presencia';
import { accionDeAviso, enviarAccion, reprogramar } from './foco-aviso';

/**
 * EL FOCO, VIGILADO DESDE EL ARMAZÓN.
 *
 * Cerrar y guardar la sesión colgaba de la pantalla de Foco: si el usuario se iba
 * a Hoy, la fase se quedaba en «corriendo» para siempre y la sesión no se
 * guardaba. Esto está montado siempre y no pinta nada.
 */
export function VigiaFoco(): null {
  const fase = useFocus((s) => s.phase);
  const endsAt = useFocus((s) => s.endsAt);
  const sesionId = useFocus((s) => s.sesionId);
  const previa = useRef(fase);
  const anteriorAviso = useRef<{ phase: typeof fase; sesionId: string | null }>({
    phase: fase,
    sesionId,
  });

  // Cierra la sesión a su hora, esté donde esté el usuario.
  useEffect(() => {
    if (fase !== 'running' || endsAt === null) return;
    let id: ReturnType<typeof setTimeout>;
    const vigilar = () => {
      useFocus.getState().tick();
      if (useFocus.getState().phase === 'running') {
        id = setTimeout(vigilar, Math.max(250, endsAt - Date.now()));
      }
    };
    id = setTimeout(vigilar, Math.max(0, endsAt - Date.now()));
    // Una pestaña de fondo retrasa los timeouts: al volver se mira el reloj.
    const alVolver = () => {
      if (document.visibilityState === 'visible') useFocus.getState().tick();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      clearTimeout(id);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [fase, endsAt]);

  // El aviso push del fin: se programa al empezar o seguir y se cancela al pausar,
  // parar o terminar antes. Va ANTES del guardado, que vacía la sesión: la foto que
  // se mira es la de este render.
  useEffect(() => {
    const antes = anteriorAviso.current;
    anteriorAviso.current = { phase: fase, sesionId };
    if (antes.phase === fase && antes.sesionId === sesionId) return;
    const foto = useFocus.getState();
    enviarAccion(accionDeAviso(antes, { ...foto, phase: fase, sesionId, endsAt }, Date.now()));
  }, [fase, sesionId, endsAt]);

  // Si al empezar no había red, se vuelve a programar al recuperarla o al volver a
  // la pestaña. El servidor lo trata como la misma sesión: no duplica.
  useEffect(() => {
    if (fase !== 'running') return;
    const otraVez = () => {
      if (document.visibilityState !== 'visible') return;
      enviarAccion(reprogramar(useFocus.getState(), Date.now()));
    };
    window.addEventListener('online', otraVez);
    document.addEventListener('visibilitychange', otraVez);
    return () => {
      window.removeEventListener('online', otraVez);
      document.removeEventListener('visibilitychange', otraVez);
    };
  }, [fase]);

  // Al terminar, la sesión se guarda sola: si hubiera que confirmarla, se
  // perderían justo las que más cuesta hacer.
  useEffect(() => {
    if (fase !== 'done') return;
    void (async () => {
      const { focusCyclesBeforeLong } = await getSettings();
      // `cerrar` vacía y avanza la tanda en un solo paso: un segundo disparo del
      // efecto ya no la ve y no la guarda dos veces (los ids no son deterministas).
      const sesion = useFocus.getState().cerrar(focusCyclesBeforeLong);
      // Un descanso no se guarda: no es foco y no puntúa.
      if (sesion?.tipo === 'foco' && sesion.startedAt !== null) {
        await guardar({ ...sesion, startedAt: sesion.startedAt });
      }
    })();
  }, [fase]);

  // La presencia del círculo sigue a las transiciones, no al estado: al recargar
  // con una sesión abierta no se vuelve a anunciar nada.
  useEffect(() => {
    const antes = previa.current;
    previa.current = fase;
    if (antes === fase) return;
    if (fase === 'running') {
      // Un descanso no es «enfocando»: el círculo no tiene por qué esperar.
      if (useFocus.getState().tipo === 'descanso') return;
      const restante = Math.ceil(useFocus.getState().remainingMs / 60_000);
      avisarPresencia('enfocando', Math.min(180, Math.max(1, restante)));
    } else if (antes === 'running') {
      avisarPresencia('disponible');
    }
  }, [fase]);

  return null;
}

async function guardar(sesion: {
  habitId: string | null;
  areaId: string | null;
  label: string;
  startedAt: string;
  plannedMinutes: number;
  workedMs: number;
}): Promise<void> {
  // Los ajustes se leen de la base y no de un hook: una sesión que terminó con la
  // app cerrada se guarda al arrancar, antes de que los ajustes estén cargados.
  const ajustes = await getSettings();
  const trabajados = Math.round(sesion.workedMs / 60_000);
  await recordFocusSession({
    habitId: sesion.habitId,
    areaId: sesion.areaId,
    label: sesion.label || 'Foco',
    // El día es el de cuando empezó, no el de cuando se guarda.
    localDate: logicalDate(new Date(sesion.startedAt), ajustes.tz, ajustes.dayStartOffsetMin),
    startedAt: sesion.startedAt,
    plannedMinutes: sesion.plannedMinutes,
    actualMinutes: trabajados,
    interrupted: trabajados < sesion.plannedMinutes * 0.8,
    tz: ajustes.tz,
  });
}
